import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Columns3, ChevronDown, CheckSquare, Square } from "lucide-react";
import { TableColumn } from "../Table";

interface ColumnSelectorProps {
  columns: TableColumn<any>[];
  visibleKeys: string[];
  onToggle: (key: string) => void;
  className?: string;
}

const MENU_WIDTH = 256;
const GAP = 8;

function computeMenuLayout(trigger: HTMLButtonElement): {
  top: number;
  left: number;
  listMaxHeight: number;
} {
  const rect = trigger.getBoundingClientRect();
  let left = rect.right - MENU_WIDTH;
  left = Math.max(GAP, Math.min(left, window.innerWidth - MENU_WIDTH - GAP));
  const top = rect.bottom + GAP;
  const headerReserve = 52;
  const listMaxHeight = Math.max(
    120,
    Math.min(400, window.innerHeight - top - GAP - headerReserve),
  );
  return { top, left, listMaxHeight };
}

// const MENU_WIDTH = 256;
// const MENU_OFFSET = 8;

/**
 * Renders the column visibility selector trigger and a portaled dropdown
 * that escapes parent overflow/clipping contexts (e.g. table cards using
 * `overflow-hidden`). The dropdown floats above page content but stays below
 * sticky page headers/sidebars.
 */
export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleKeys,
  onToggle,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [menuLayout, setMenuLayout] = useState<{
    top: number;
    left: number;
    listMaxHeight: number;
  } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const syncLayout = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn || !isOpen) return;
    setMenuLayout(computeMenuLayout(btn));
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuLayout(null);
      return;
    }
    syncLayout();
  }, [isOpen, syncLayout, columns.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => syncLayout();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, syncLayout]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current?.contains(t) ||
        menuRef.current?.contains(t)
      ) {
        return;
      }
      setIsOpen(false);
    };

    // const handleEscape = (e: KeyboardEvent) => {
    //   if (e.key === "Escape") setIsOpen(false);
    // };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const menuPortal =
    mounted &&
    isOpen &&
    menuLayout &&
    createPortal(
      <div
        ref={menuRef}
        className="fixed w-64 rounded-xl border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] py-2 shadow-xl z-[1100] flex flex-col animate-in fade-in zoom-in-95 duration-200 overscroll-contain"
        style={{
          top: menuLayout.top,
          left: menuLayout.left,
          transformOrigin: "top right",
        }}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-[var(--color-border-primary)] px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-widest">
            Manage Visibility
          </span>
        </div>
        <div
          className="overflow-y-auto overscroll-contain py-1 scrollbar-thin scrollbar-thumb-[var(--color-border-primary)]"
          style={{ maxHeight: menuLayout.listMaxHeight }}
          onWheel={(e) => e.stopPropagation()}
        >
          {columns.map((col) => {
            const isRequired = !!col.required;
            const isVisible = isRequired || visibleKeys.includes(col.key);

            return (
              <label
                key={col.key}
                className={`
                    flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer transition-colors
                    ${isRequired ? "opacity-60 cursor-not-allowed bg-[var(--color-bg-muted)]" : "hover:bg-[var(--color-bg-muted)] active:bg-[var(--color-bg-muted)]"}
                  `}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={isVisible}
                  onChange={() => !isRequired && onToggle(col.key)}
                  disabled={isRequired}
                />

                {isVisible ? (
                  <CheckSquare
                    size={18}
                    className="text-primary-600 fill-primary-50"
                  />
                ) : (
                  <Square size={18} className="text-[var(--color-text-tertiary)]" />
                )}

                <span
                  className={`flex-1 truncate ${isVisible ? "text-[var(--color-text-primary)] font-medium" : "text-[var(--color-text-tertiary)] font-normal"}`}
                >
                  {col.label ||
                    (typeof col.title === "string" ? col.title : col.key)}
                </span>

                {isRequired && (
                  <span className="text-[9px] bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)] px-1.5 py-0.5 rounded font-black uppercase tracking-tighter shrink-0">
                    Required
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>,
      document.body,
    );

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((o) => !o)}
        className="
          flex h-10 items-center gap-2 px-[13px] text-label font-semibold
          text-[var(--color-text-strong)] bg-[var(--color-bg-base)] border border-[var(--color-border-control)] rounded-[11px]
          hover:border-[var(--color-border-input-hover)] transition-all duration-200
        "
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Columns3 size={15} className="text-[var(--color-text-tertiary)]" />
        <span>Columns</span>
        <ChevronDown
          size={15}
          className={`text-[var(--color-text-tertiary)] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {menuPortal}
    </div>
  );
};